import _isUndefined from "lodash/isUndefined";
import _find from "lodash/find";
import _get from "lodash/get";
import { getInstitution, getProduct } from './common';
/**
 * Get the TEMPORARILY exposed, client derived details object.
 *
 * Only usable by clients who leverage `augmentMortgagesMarketplaceOfferWithClientDerivedDetails` utility on marketplace entities.
 * @see `../utils/mortgages/fetch.js`
 *
 * @param {Object} augmentedMarketplaceEntity
 * @returns {Object} - The client augmented details.
 */

var getClientDerivedDetails = function getClientDerivedDetails(augmentedMarketplaceEntity) {
  return _get(getProduct(augmentedMarketplaceEntity), '__client_derived_details');
};
/**
 * DEPRECATED SELECTORS
 */

/**
 * Get the associated marketplace data on a lender.
 * @deprecated
 * @private
 *
 * @param {Object} wpLenderObject - The (augmented) WordPress lender entity
 * @return {Object} - wpLenderObject.marketplace_lender_data
 */


var getMarketplaceLenderData = function getMarketplaceLenderData(wpLenderObject) {
  return _get(wpLenderObject, 'marketplace_lender_data');
};
/**
 * Get the associated lender data on a lender.
 * @deprecated
 * @private
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {Object} - wpLenderObject.nw_lender_data
 */


var getNwLenderData = function getNwLenderData(wpLenderObject) {
  return _get(wpLenderObject, 'nw_lender_data');
};
/**
 * Get the review url on a mortgage lender.
 * @deprecated
 * @private
 *
 * @param {Object} wpLenderObject - The WordPress lender entity.
 * @return {String} - Associated review url.
 */


var getReviewUrl = function getReviewUrl(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'review_url');
};
/**
 * Get the post id of associated review page on mortgage lender.
 * @deprecated
 * @private
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {Number} - Associated review post id
 */


var getReviewPostId = function getReviewPostId(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'review.id');
};
/**
 * Get the post type of associated review page on mortgage lender.
 * @deprecated
 * @private
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {String} - Associated review post type
 */


var getReviewPostType = function getReviewPostType(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'review.type');
};
/**
 * Get appropriate `_embedded` review data associated with a lender.
 * @deprecated
 * @private
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {Object} - `_embedded` post data based on `review.id` & `review.type`
 */


var getEmbeddedReviewData = function getEmbeddedReviewData(wpLenderObject) {
  var id = getReviewPostId(wpLenderObject);
  var postType = getReviewPostType(wpLenderObject);

  var embeddedPosts = _get(wpLenderObject, "_embedded[nw:".concat(postType, "]"));

  return _find(embeddedPosts, {
    id: id
  });
};
/**
 * Get apply disclaimer text (to be used with ApplyNow).
 * @deprecated - Use "at ${issuerName}"
 *
 * @param {Object} wpLenderObject - The WordPress lender entity.
 * @return {String} - Apply Now disclaimer text.
 */


export var getApplyDisclaimerText = function getApplyDisclaimerText(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'apply_disclaimer_text');
};
/**
 * Get apply text (to be used with ApplyNow).
 * @deprecated - Use "Learn More"
 *
 * @param {Object} wpLenderObject - The WordPress lender entity.
 * @return {String} - Apply Now text.
 */

export var getApplyText = function getApplyText(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'apply_text');
};
/**
 * Get the featured loan attributes.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity.
 * @returns {Array<String>} - The array of `MORTGAGES_FEATURED_LOAN_ATTRIBUTES`
 */

export var getFeaturedLoanAttributes = function getFeaturedLoanAttributes(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.featured_loan_attributes.value');
};
/**
 * Get loan products associated with the lender.
 * @deprecated - Use getFeaturedLoanAttributes where possible.
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {Array} - The array of `MORTGAGES_LOAN_PRODUCTS`
 */

export var getLoanProducts = function getLoanProducts(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'loan_products');
};
/**
 * Get product loan types associated with the lender.
 * @deprecated - Use getFeaturedLoanAttributes where possible.
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {Array} - The array of `MORTGAGES_LOAN_TYPES`
 */

export var getLoanTypes = function getLoanTypes(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'loan_types');
};
/**
 * Get the dictionary of min down payment information.
 * @deprecated
 *
 * @param {Object} wpLenderObject - The WordPress lender entity
 * @return {Object} - The dictionary of min down payment information
 */

export var getMinDownPaymentDictionary = function getMinDownPaymentDictionary(wpLenderObject) {
  return _get(getNwLenderData(wpLenderObject), 'min_down_payment');
};
/**
 * *** END DEPRECATED SELECTORS ***
 */

/**
 * Gets Apply Now link url.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The apply now url
 */

export var getApplyLink = function getApplyLink(marketplaceEntity) {
  var applyLink = _get(marketplaceEntity, 'derived_details.apply_link.value');

  if (applyLink) {
    return applyLink;
  } // Fallback to WordPress lender entity


  var uuid = _get(getMarketplaceLenderData(marketplaceEntity), 'uuid');

  if (!uuid) {
    return _get(getNwLenderData(marketplaceEntity), 'apply_url');
  }

  return "/redirect/".concat(uuid);
};
/**
 * Gets bottom line or "tagline" content.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The bottom line content
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.tagline.value') || // Fallback to WordPress lender entity
  _get(getNwLenderData(marketplaceEntity), 'why_we_like_it');
};
/**
 * Get the "cons" bullets.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity.
 * @return {Array} - The "cons" array.
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.cons_bullets.value') || // Fallback to WordPress lender entity
  _get(getNwLenderData(marketplaceEntity), 'cons');
};
/**
 * Get the specified drivers to display on the page.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Array} - The drivers to display
 */

export var getDrivers = function getDrivers(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.drivers.value') || // Fallback to WordPress lender entity
  _get(getNwLenderData(marketplaceEntity), 'widget_attributes');
};
/**
 * Gets the alt text for the image associated with the lender
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The image "alt" text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.display_name.value') || // Fallback to WordPress lender entity
  _get(marketplaceEntity, 'featured_media.alt_text');
};
/**
 * Get the source URL of the image associated with the lender.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The url of the image
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.image_source.value') || // Fallback to WordPress lender entity
  _get(getMarketplaceLenderData(marketplaceEntity), 'logo');
};
/**
 * Get the monetization status of the lender offer.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Boolean} - The monetization status of lender product
 */

export var getIsMonetizing = function getIsMonetizing(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.is_monetizing.value', // Fallback to WordPress lender entity
  _get(getMarketplaceLenderData(marketplaceEntity), 'is_monetizing'));
};
/**
 * Get whether to enable rates experiences for the lender offer.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Boolean} - Whether to enable rates experiences for the lender offer.
 */

export var getIsRatesEnabled = function getIsRatesEnabled(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.is_rates_enabled.value', // Fallback to WordPress lender entity
  _get(getNwLenderData(marketplaceEntity), 'personalized_rates_enabled'));
};
/**
 * Get the issuer name.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The issuer's name
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.display_name.value') || // Fallback to WordPress lender entity
  _get(getMarketplaceLenderData(marketplaceEntity), 'display_name');
};
/**
 * Gets url to learn more page.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The "learn more" page url
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.review_link.value') || // Fallback to WordPress lender entity
  getReviewUrl(marketplaceEntity) || _get(getEmbeddedReviewData(marketplaceEntity), 'link');
};
/**
 * Get the loan type.
 * @param {Object} marketplaceEntity - The marketplace entity
 * @return {String} - The loan type
 */

export var getLoanType = function getLoanType(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.loan_type.value');
};
/**
 * Get the max LTV (loan-to-value) percentage
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The max LTV
 */

export var getMaxLtvText = function getMaxLtvText(marketplaceEntity) {
  var maxLtvPercentage = _get(marketplaceEntity, 'details.max_ltv.value');

  if (!_isUndefined(maxLtvPercentage)) {
    return maxLtvPercentage;
  } // Fallback to WordPress lender entity


  return _get(getNwLenderData(marketplaceEntity), 'max_ltv.text');
};
/**
 * Get the min credit score.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min credit score
 */

export var getMinCreditScore = function getMinCreditScore(marketplaceEntity) {
  var creditScore = _get(marketplaceEntity, 'details.min_credit_score.value');

  if (creditScore) {
    return creditScore;
  } // Fallback to WordPress lender entity


  var minCreditScoreDictionary = getMinCreditScoreDictionary(marketplaceEntity);

  var type = _get(minCreditScoreDictionary, 'text');

  return _get(minCreditScoreDictionary, type) || _get(minCreditScoreDictionary, 'conventional') || _get(minCreditScoreDictionary, 'va') || _get(minCreditScoreDictionary, 'fha');
};
/**
 * Get the min credit score override that should display if within the tooltip.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min credit score tooltip override
 */

export var getMinCreditScoreTooltip = function getMinCreditScoreTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_credit_score_tooltip.value');
};
/**
 * Get the dictionary of min credit score information.
 * Uses temporarily added `__client_derived_details`.
 *
 * @param {Object} augmentedMarketplaceEntity - The Marketplace entity, augmented with `__client_derived_details`.
 * @return {Object} - The dictionary of min credit score information
 */

export var getMinCreditScoreDictionary = function getMinCreditScoreDictionary(augmentedMarketplaceEntity) {
  var clientDerivedMinCreditScores = _get(getClientDerivedDetails(augmentedMarketplaceEntity), 'min_credit_scores');

  if (clientDerivedMinCreditScores) {
    return clientDerivedMinCreditScores;
  } // Fallback to WordPress lender entity


  return _get(getNwLenderData(augmentedMarketplaceEntity), 'min_credit_score');
};
/**
 * Get the list of credit score types to include in the tooltip.
 * Uses temporarily added `__client_derived_details`.
 *
 * @param {Object} augmentedMarketplaceEntity - The Marketplace entity, augmented with `__client_derived_details`.
 * @return {Array} - The list of credit score types to include in the tooltip
 */

export var getMinCreditScoreTypesForTooltip = function getMinCreditScoreTypesForTooltip(augmentedMarketplaceEntity) {
  var clientDerivedMinCreditTypes = _get(getClientDerivedDetails(augmentedMarketplaceEntity), 'featured_loan_types');

  if (clientDerivedMinCreditTypes) {
    return clientDerivedMinCreditTypes;
  } // Fallback to WordPress lender entity


  return _get(getMinCreditScoreDictionary(augmentedMarketplaceEntity), 'tooltip');
};
/**
 * Get the min down payment.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min down payment
 */

export var getMinDownPayment = function getMinDownPayment(marketplaceEntity) {
  var downPayment = _get(marketplaceEntity, 'details.min_down_payment.value');

  if (downPayment) {
    return downPayment;
  } // Fallback to WordPress lender entity


  var minDownPaymentDictionary = getMinDownPaymentDictionary(marketplaceEntity);
  var type = _get(minDownPaymentDictionary, 'text') || 'conventional';
  return _get(minDownPaymentDictionary, type) || _get(minDownPaymentDictionary, 'conventional') || _get(minDownPaymentDictionary, 'va') || _get(minDownPaymentDictionary, 'fha');
};
/**
 * Get the min down payment tooltip text.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The min down payment tooltip text
 */

export var getMinDownPaymentTooltip = function getMinDownPaymentTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_down_payment_tooltip.value') || // Fallback to WordPress lender entity
  _get(getMinDownPaymentDictionary(marketplaceEntity), 'tooltip');
};
/**
 * Get the lender id.
 * @deprecated
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The lender id
 */

export var getLenderId = function getLenderId(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.lender_id.value') || // Fallback to WordPress lender entity
  _get(getMarketplaceLenderData(marketplaceEntity), 'lender_id');
};
/**
 * Get the mortech id.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The mortech id
 */

export var getMortechId = function getMortechId(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.mortech_id.value');
};
/**
 * Get the name to display for a lender.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The lender name
 */

export var getName = function getName(marketplaceEntity) {
  return getIssuerName(marketplaceEntity);
};
/**
 * Gets NMLS ID (Nationwide Mortgage Licensing System and Registry)
 *
 * @param {Object} marketplaceEntity - The Marketplace entity.
 * @return {String} - The NMLS id.
 */

export var getNmlsId = function getNmlsId(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.nmls_id.value') || // Fallback to WordPress lender entity
  _get(getMarketplaceLenderData(marketplaceEntity), 'nmls_id');
};
/**
 * Get whether the mortgage lender is national/regional.
 *
 * @param   {Object} wpLenderObject   The WordPress lender entity
 * @return  {string}                  "National" or "Regional"
 */

export var getNationalOrRegional = function getNationalOrRegional(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.coverage_area.value') || // Fallback to WordPress lender entity
  _get(getNwLenderData(marketplaceEntity), 'national_regional.text');
};
/**
 * Get the "pros" bullets.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Array<String>} - The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.pros_bullets.value') || // Fallback to WordPress lender entity
  _get(getNwLenderData(marketplaceEntity), 'pros');
};
/**
 * Get the slug to identify the marketplace entity.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {String} - The slug
 */

export var getSlug = function getSlug(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'external_id') || // Fallback to WordPress lender entity
  _get(marketplaceEntity, 'slug');
};
/**
 * Get the star rating.
 *
 * @param {Object} marketplaceEntity - The Marketplace entity
 * @return {Number} - The star rating
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  var nwRating = _get(marketplaceEntity, 'details.rating_stars.value');

  if (nwRating) {
    return nwRating;
  } // Fallback to WordPress lender entity


  var rating = _get(getNwLenderData(marketplaceEntity), 'rating') || _get(getEmbeddedReviewData(marketplaceEntity), 'nw_schema.data.rating') || _get(getEmbeddedReviewData(marketplaceEntity), 'nw_review_data.rating');

  return typeof rating !== 'undefined' ? parseFloat(rating) : undefined;
};
/**
 * Get the star rating tooltip.
 *
 * @return {string} - The star rating tooltip
 */

export var getStarRatingTooltip = function getStarRatingTooltip() {
  return "NerdWallet's ratings are determined by our editorial team. The scoring formula takes into account loan types and loan products offered, online conveniences, online mortgage rate information, and the rate spread and origination fee lenders reported in the latest available HMDA data.";
};